import { Box, Typography, Button, Chip, Paper, Divider } from "@mui/material";
import { useRecordContext } from "react-admin";
import { generateDarkVariant, isValidHexColor } from "@/utils/colorUtils";
import { pickBranding } from "@/utils/brandingSync";

/**
 * Live preview of partner branding colors as they would appear in the app.
 * Shows both light and dark mode previews side-by-side.
 * Mirrors the actual SolarLayout with a side navigation rail.
 *
 * Reads colors directly from the current record context so it updates
 * in real-time as the partner saves color changes via EditableGrid.
 */
export const BrandingPreview = () => {
  const record = useRecordContext();

  // Canonical dedicated branding column, treating empty `{}` as absent (#4116).
  const branding = pickBranding(record);
  const primary = branding?.brand_primary;
  const secondary = branding?.brand_secondary;

  if (!primary || !isValidHexColor(primary)) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Set a primary brand color above to see a live preview.
        </Typography>
      </Box>
    );
  }

  const effectiveSecondary = isValidHexColor(secondary) ? secondary : primary;

  const primaryDark =
    branding?.brand_primary_dark && isValidHexColor(branding.brand_primary_dark)
      ? branding.brand_primary_dark
      : generateDarkVariant(primary);
  const secondaryDark =
    branding?.brand_secondary_dark && isValidHexColor(branding.brand_secondary_dark)
      ? branding.brand_secondary_dark
      : generateDarkVariant(effectiveSecondary);

  return (
    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
      <PreviewCard
        title="Light Mode"
        primary={primary}
        secondary={effectiveSecondary}
        bg="#FFFFFF"
        surfaceBg="#F5F5F5"
        textPrimary="#1A1A1A"
        textSecondary="#6E6E6E"
        primaryContrastText="#FFFFFF"
        secondaryContrastText="#FFFFFF"
      />
      <PreviewCard
        title="Dark Mode"
        primary={primaryDark}
        secondary={secondaryDark}
        bg="#1E1E1E"
        surfaceBg="#121212"
        textPrimary="#FFFFFF"
        textSecondary="#BCC0CC"
        primaryContrastText="#000000"
        secondaryContrastText="#000000"
      />
    </Box>
  );
};

interface PreviewCardProps {
  title: string;
  primary: string;
  secondary: string;
  bg: string;
  surfaceBg: string;
  textPrimary: string;
  textSecondary: string;
  primaryContrastText: string;
  secondaryContrastText: string;
}

const PreviewCard = ({
  title,
  primary,
  secondary,
  bg,
  surfaceBg,
  textPrimary,
  textSecondary,
  primaryContrastText,
  secondaryContrastText,
}: PreviewCardProps) => (
  <Paper
    variant="outlined"
    sx={{
      flex: "1 1 380px",
      maxWidth: 480,
      overflow: "hidden",
      borderRadius: 2,
    }}
  >
    {/* Section label */}
    <Box sx={{ px: 2, py: 1, bgcolor: "background.default" }}>
      <Typography variant="caption" fontWeight={600} color="text.secondary">
        {title}
      </Typography>
    </Box>

    <Divider />

    {/* App layout: side nav + content */}
    <Box sx={{ display: "flex", bgcolor: surfaceBg, minHeight: 220 }}>
      {/* Side nav rail (mirrors SolarLayout) */}
      <Box
        sx={{
          width: 48,
          bgcolor: primary,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 1.5,
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        {/* Logo placeholder */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: `${primaryContrastText}25`,
            mb: 1,
          }}
        />
        {/* Nav items */}
        {[false, true, false, false, false].map((selected, i) => (
          <Box
            key={i}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: selected ? secondary : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.5,
                bgcolor: selected ? `${secondaryContrastText}CC` : `${primaryContrastText}55`,
              }}
            />
          </Box>
        ))}

        {/* Bottom spacer + user avatar */}
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            border: `1.5px solid ${primaryContrastText}55`,
          }}
        />
      </Box>

      {/* Content area */}
      <Box sx={{ flex: 1, p: 1.5 }}>
        <Box sx={{ bgcolor: bg, borderRadius: 1, p: 1.5 }}>
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
            <Typography sx={{ color: primary, fontSize: "10px", fontWeight: 500 }}>Partners</Typography>
            <Typography sx={{ color: textSecondary, fontSize: "10px" }}>/</Typography>
            <Typography sx={{ color: textSecondary, fontSize: "10px" }}>Acme Insurance</Typography>
          </Box>

          {/* Page heading */}
          <Typography
            sx={{
              color: textPrimary,
              fontSize: "14px",
              fontWeight: 600,
              mb: 1,
            }}
          >
            Claims Platform
          </Typography>

          {/* Stat cards row */}
          <Box sx={{ display: "flex", gap: 0.75, mb: 1.25 }}>
            {["12 Open", "5 Pending", "87 Closed"].map((stat, i) => (
              <Box
                key={stat}
                sx={{
                  flex: 1,
                  py: 0.5,
                  px: 0.75,
                  borderRadius: 1,
                  bgcolor: i === 0 ? `${primary}12` : `${surfaceBg}`,
                  border: "1px solid",
                  borderColor: i === 0 ? `${primary}30` : `${textSecondary}20`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: i === 0 ? primary : textPrimary,
                  }}
                >
                  {stat.split(" ")[0]}
                </Typography>
                <Typography sx={{ fontSize: "9px", color: textSecondary }}>{stat.split(" ")[1]}</Typography>
              </Box>
            ))}
          </Box>

          {/* Buttons */}
          <Box sx={{ display: "flex", gap: 0.75, mb: 1 }}>
            <Button
              size="small"
              sx={{
                bgcolor: primary,
                color: primaryContrastText,
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "none",
                px: 1.25,
                py: 0.4,
                borderRadius: 1,
                minWidth: 0,
                lineHeight: 1.4,
                "&:hover": { bgcolor: primary, opacity: 0.9 },
              }}
            >
              Primary
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={{
                borderColor: primary,
                color: primary,
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "none",
                px: 1.25,
                py: 0.4,
                borderRadius: 1,
                minWidth: 0,
                lineHeight: 1.4,
              }}
            >
              Outlined
            </Button>
            <Button
              size="small"
              sx={{
                bgcolor: secondary,
                color: secondaryContrastText,
                fontSize: "10px",
                fontWeight: 600,
                textTransform: "none",
                px: 1.25,
                py: 0.4,
                borderRadius: 1,
                minWidth: 0,
                lineHeight: 1.4,
                "&:hover": { bgcolor: secondary, opacity: 0.9 },
              }}
            >
              Secondary
            </Button>
          </Box>

          {/* Chips */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Chip
              label="Active"
              size="small"
              sx={{
                height: 18,
                fontSize: "9px",
                fontWeight: 600,
                bgcolor: `${primary}18`,
                color: primary,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
            <Chip
              label="In Review"
              size="small"
              sx={{
                height: 18,
                fontSize: "9px",
                fontWeight: 600,
                bgcolor: `${secondary}18`,
                color: secondary,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
            <Chip
              label="Draft"
              size="small"
              sx={{
                height: 18,
                fontSize: "9px",
                bgcolor: `${textSecondary}15`,
                color: textSecondary,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  </Paper>
);
